<template>
  <div class="security-sensitive-verify-host">
    <el-dialog
      :visible.sync="dialogVisible"
      title=""
      width="560px"
      append-to-body
      custom-class="sensitive-verify-dialog sensitive-verify-dialog--combined"
      @closed="onDialogClosed"
      :close-on-click-modal="false"
 
    >
      <div class="verify-body verify-body--combined">
        <p class="verify-head-msg">
          您正在进行重要操作，为保证您的数据安全，请进行安全验证
        </p>

        <!-- 账号展示 -->
        <div class="labeled-row">
          <span class="row-label">迷你号</span>
          <div class="readonly-field">{{ displayAccountId }}</div>
        </div>

        <!-- 独立密码 -->
        <div v-if="showPwd" class="labeled-row">
          <span class="row-label">二级密码</span>
          <el-input
            v-model="sensitivePwd"
            type="password"
            show-password
            placeholder="请输入二级密码"
            maxlength="32"
            @keyup.enter.native="handleConfirm"
          />
        </div>

        <!-- 手机号：有绑定手机则脱敏展示，否则允许用户输入 -->
        <div v-if="showSms && maskedPhone !== '—'" class="labeled-row">
          <span class="row-label">手机号</span>
          <div v-if="maskedPhone !== '—'" class="readonly-field">
            {{ maskedPhone }}
          </div>
        </div>

        <!-- 短信验证码 -->
        <div v-if="showSms" class="labeled-row">
          <span class="row-label">验证码</span>
          <div class="sms-row">
            <el-input
              v-model="sensitiveSmsCode"
              placeholder="请输入验证码"
              maxlength="8"
              @keyup.enter.native="handleConfirm"
            />
            <el-button
              type="warning"
              :disabled="smsBtnDisabled"
              class="btn-code"
              @click="sendSensitiveSms"
              >{{ smsBtnTxt }}</el-button
            >
          </div>
        </div>

        <el-button
          type="warning"
          class="btn-main"
          :loading="submitting"
          @click="handleConfirm"
          >确认</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  registerSecuritySensitiveVerifyHost,
  unregisterSecuritySensitiveVerifyHost,
} from '@/utils/securitySensitiveVerify';
import { getVercode, checkVercode, getVercodeUnlogin } from '@/api/common';
import { authSecPw } from '@/api/accountSecurity';
import { encryptPwd } from '@/utils/crypto';

export default {
  name: 'SecuritySensitiveVerifyHost',
  data() {
    return {
      dialogVisible: false,
      flowActive: false,
      /** 'single' 独立调接口校验*/
      mode: '',
      showPwd: false,
      showSms: false,
      sensitivePwd: '',
      sensitiveSmsCode: '',
      inputPhone: '',
      smsBtnTxt: '获取验证码',
      smsBtnDisabled: false,
      smsTimer: null,
      submitting: false,
      /** 登录模式下外部传入，覆盖 Vuex userInfo/developerInfo */
      overrideUin: '',
      overridePhone: '',
      resolveCallback: null,
      rejectCallback: null,
    };
  },
  computed: {
    ...mapState(['userInfo', 'developerInfo']),
    displayAccountId() {
      if (this.overrideUin) return this.overrideUin;
      const u = this.userInfo && this.userInfo.uin;
      return u != null && u !== '' ? String(u) : '—';
    },
    maskedPhone() {
      const raw =
        this.overridePhone || (this.developerInfo && this.developerInfo.phone);
      if (!raw) return '—';
      const s = String(raw);
      if (s.length <= 4) return s;
      return `${s.slice(0, 2)}*******${s.slice(-2)}`;
    },
  },
  mounted() {
    registerSecuritySensitiveVerifyHost(this);
  },
  beforeDestroy() {
    unregisterSecuritySensitiveVerifyHost(this);
    this.clearSmsCountDown();
  },
  methods: {
    /**
     * @param {object} store  Vuex store
     * @param {Function} resolve
     * @param {Function} reject
     * @param {object} [options]
     * @param {'verify'|'login'} [options.mode='verify']
     *   - verify：弹窗内部独立调接口验证，通过后 resolve()
     *   - login ：不调内部接口，resolve({ sec_pw, pcode }) 把值透传给调用方
     * @param {boolean} [options.onlySms=false] 是否只进行短信验证
     */
    beginSensitiveVerify(store, resolve, reject, options = {}) {
      this.cleanupPromiseOnly();
      const sec = store.getters.accountSecurity || {};
      const independentPwdOn = !!sec.independentPwdOn;
      const smsOn = !!sec.smsOn;
      const phone =
        store.state.developerInfo && store.state.developerInfo.phone;

      const { onlySms = false, mode = 'verify' } = options;

      if (!onlySms && !independentPwdOn && !smsOn) {
        resolve({});
        return;
      }

      this.resolveCallback = resolve;
      this.rejectCallback = reject;
      this.flowActive = true;
      this.mode = mode;
      this.showPwd = independentPwdOn;
      this.showSms = onlySms ? true : smsOn;
      this.sensitivePwd = '';
      this.sensitiveSmsCode = '';
      this.inputPhone = '';
      this.submitting = false;
      this.overrideUin = String(
        (store.state.userInfo && store.state.userInfo.uin) || '',
      );
      this.overridePhone = String(phone || '');
      this.clearSmsCountDown();
      this.dialogVisible = true;
    },

    async handleConfirm() {
      if (this.showPwd && !this.sensitivePwd) {
        this.$message.warning('请输入二级密码');
        return;
      }
      if (this.showSms) {
        if (!this.sensitiveSmsCode) {
          this.$message.warning('请输入验证码');
          return;
        }
      }

      if (this.mode !== 'single') {
        const payload = {};
        if (this.showPwd) payload.sec_pw = encryptPwd(this.sensitivePwd);
        if (this.showSms) {
          payload.pcode = this.sensitiveSmsCode;
          const phone =
            (this.developerInfo && this.developerInfo.phone) ||
            this.overridePhone ||
            this.inputPhone;
          if (phone) payload.phone = phone;
        }
        this.finishOk(payload);
        return;
      }

      // 独立校验模式
      this.submitting = true;
      try {
        if (this.showPwd) {
          await authSecPw({
            uin: this.userInfo && this.userInfo.uin,
            password: this.sensitivePwd,
          });
        }
        if (this.showSms) {
          const phone =
            (this.developerInfo && this.developerInfo.phone) ||
            this.overridePhone ||
            this.inputPhone;
          await checkVercode({
            vercode: this.sensitiveSmsCode,
            phone,
            tk: this.userInfo && this.userInfo.tk,
          });
        }
        this.finishOk({});
      } catch (e) {
        console.error(e);
      } finally {
        this.submitting = false;
      }
    },

    async sendSensitiveSms() {
      if (this.smsBtnDisabled) return;
      const phone =
        (this.developerInfo && this.developerInfo.phone) ||
        this.overridePhone ||
        this.inputPhone;
      try {
        if (this.userInfo && this.userInfo.tk) {
          await getVercode({
            phone,
            tk: this.userInfo && this.userInfo.tk,
            uin: this.displayAccountId,
          });
        } else {
          await getVercodeUnlogin({
            phone,
            uin: this.displayAccountId,
          });
        }
        this.$message.success('发送成功');
        this.startSmsCountDown();
      } catch (e) {
        console.error(e);
      }
    },

    startSmsCountDown() {
      this.clearSmsCountDown();
      let t = 60;
      this.smsBtnDisabled = true;
      this.smsBtnTxt = `重新发送${t}S`;
      this.smsTimer = setInterval(() => {
        t--;
        if (t > 0) {
          this.smsBtnTxt = `重新发送${t}S`;
        } else {
          this.clearSmsCountDown();
        }
      }, 1000);
    },

    clearSmsCountDown() {
      if (this.smsTimer) {
        clearInterval(this.smsTimer);
        this.smsTimer = null;
      }
      this.smsBtnDisabled = false;
      this.smsBtnTxt = '获取验证码';
    },

    onDialogClosed() {
      if (!this.flowActive) return;
      this.cancelFlow();
    },

    finishOk(payload) {
      const fn = this.resolveCallback;
      this.resetFlow();
      this.dialogVisible = false;
      if (fn) fn(payload);
    },

    cancelFlow() {
      const fn = this.rejectCallback;
      this.resetFlow();
      if (fn) fn(new Error('SECURITY_VERIFY_CANCEL'));
    },

    cleanupPromiseOnly() {
      this.resolveCallback = null;
      this.rejectCallback = null;
    },

    resetFlow() {
      this.flowActive = false;
      this.mode = '';
      this.showPwd = false;
      this.showSms = false;
      this.submitting = false;
      this.overrideUin = '';
      this.overridePhone = '';
      this.inputPhone = '';
      this.clearSmsCountDown();
      this.cleanupPromiseOnly();
    },
  },
};
</script>

<!-- append-to-body 弹窗不在组件根下，样式勿用 scoped -->
<style lang="scss">
$orange: #fd9b21;

.security-sensitive-verify-host {
  display: none;
}

.el-dialog.sensitive-verify-dialog .el-dialog__header {
  display: none;
}

.el-dialog.sensitive-verify-dialog .el-dialog__body {
  padding: 28px 32px 32px;
}

.el-dialog.sensitive-verify-dialog .verify-body--combined {
  color: #606266;
}

.el-dialog.sensitive-verify-dialog .verify-head-msg {
  margin: 0 0 24px;
  font-size: 14px;
  color: #606266;
  line-height: 22px;
}

.el-dialog.sensitive-verify-dialog .labeled-row {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
}

.el-dialog.sensitive-verify-dialog .labeled-row .row-label {
  width: 72px;
  flex-shrink: 0;
  font-size: 14px;
  color: #606266;
}

.el-dialog.sensitive-verify-dialog .readonly-field {
  flex: 1;
  height: 40px;
  line-height: 40px;
  padding: 0 15px;
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #909399;
  font-size: 14px;
}

.el-dialog.sensitive-verify-dialog .labeled-row .el-input {
  flex: 1;
}

.el-dialog.sensitive-verify-dialog .sms-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.el-dialog.sensitive-verify-dialog .sms-row .el-input {
  flex: 1;
  max-width: 260px;
}

.el-dialog.sensitive-verify-dialog .sms-row .btn-code {
  height: 40px;
  border-radius: 8px;
  min-width: 120px;
  background: $orange;
  border-color: $orange;
  color: #fff;
}

.el-dialog.sensitive-verify-dialog .sms-row .el-button.is-disabled {
  background: #d9d9d9 !important;
  border-color: #d9d9d9 !important;
}

.el-dialog.sensitive-verify-dialog .btn-main {
  width: 100%;
  margin-top: 12px;
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
  background: $orange;
  border-color: $orange;
  color: #fff;
}
</style>
